'use client';

import { useEffect } from 'react';

type SeverityLevel = 0 | 1 | 2 | 3 | 4;

type AppInsightsInstance = {
  trackException: (args: {
    exception: Error;
    severityLevel?: SeverityLevel;
    properties?: Record<string, unknown>;
  }) => void;
  trackEvent: (event: { name: string }, properties?: Record<string, unknown>) => void;
  trackMetric: (metric: { name: string; average: number }, properties?: Record<string, unknown>) => void;
  trackPageView: (args: { name: string; properties?: Record<string, unknown> }) => void;
  loadAppInsights?: () => void;
  addTelemetryInitializer?: (callback: (item: unknown) => boolean) => void;
};

let appInsightsInstance: AppInsightsInstance | undefined;
let initPromise: Promise<AppInsightsInstance | undefined> | undefined;

/** Playwright / regression agents should append this to userAgent (see docs/client-telemetry.md). */
export const REGRESSION_TEST_USER_AGENT_MARKER = 'SWD-RegressionTest';

function getRegressionTestUserAgentMarker(): string {
  return process.env.NEXT_PUBLIC_APPINSIGHTS_IGNORE_USER_AGENT_SUBSTRING ?? REGRESSION_TEST_USER_AGENT_MARKER;
}

/** Skip client telemetry for automated regression sessions (userAgent or ?swd_regression=true). */
export function shouldSendClientTelemetry(): boolean {
  if (typeof window === 'undefined') return true;
  const marker = getRegressionTestUserAgentMarker();
  if (marker && navigator.userAgent.includes(marker)) return false;
  try {
    if (new URLSearchParams(window.location.search).get('swd_regression') === 'true') return false;
  } catch {
    /* noop */
  }
  return true;
}

async function initAppInsights(): Promise<AppInsightsInstance | undefined> {
  if (typeof window === 'undefined') return undefined;
  if (appInsightsInstance) return appInsightsInstance;
  if (initPromise) return initPromise;

  const connectionString = process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING;
  if (!connectionString) return undefined; // client telemetry disabled by env

  initPromise = import('@microsoft/applicationinsights-web').then(({ ApplicationInsights }) => {
    if (appInsightsInstance) return appInsightsInstance; // guard against double-init

    // Determine client sampling percentage (0-100). Only NEXT_PUBLIC_ is available in the browser.
    const samplingEnv = process.env.NEXT_PUBLIC_APPINSIGHTS_SAMPLING_PERCENTAGE;
    const samplingParsed = Number(samplingEnv);
    const samplingPct = Number.isFinite(samplingParsed) ? Math.min(100, Math.max(0, samplingParsed)) : 100;

    // Report sampling to the in-app debug overlay (avoid console noise for general users)
    try {
      const api = (
        window as unknown as {
          selfserviceDebug?: { log: (type: string, details?: string) => void };
        }
      ).selfserviceDebug;
      const msg = `[Telemetry][client] NEXT_PUBLIC_APPINSIGHTS_SAMPLING_PERCENTAGE=${
        samplingEnv ?? '(unset)'
      } → effective=${samplingPct}%`;
      api?.log('info', msg);
    } catch {
      /* noop */
    }

    const ai: AppInsightsInstance = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false, // disabled by default (no page view tracking)
        enableCorsCorrelation: false,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        disableCookiesUsage: true, // disable ai_user/ai_session cookies for GDPR compliance
        samplingPercentage: samplingPct,
        disablePageUnloadEvents: ['unload'], // prevent deprecated unload handler (fixes bfcache)
      },
    });

    // Ensure the underlying SDK is fully initialized.
    ai.loadAppInsights?.();

    // Drop Next.js RSC prefetch / client navigation fetches from dependency telemetry.
    // Each link prefetch sends a GET with ?_rsc=...; on content-heavy pages (e.g. /magazin)
    // this can produce hundreds of RemoteDependency items and has been observed to crash the tab.
    const dropRemoteOnWirFuersKlima = (item: { baseType?: string }) => {
      if (typeof window === 'undefined' || !window.location.pathname.startsWith('/wirfuersklima')) {
        return true;
      }
      if (item.baseType === 'RemoteDependencyData') {
        return false;
      }
      return true;
    };

    ai.addTelemetryInitializer?.((item: any) => {
      if (!shouldSendClientTelemetry()) return false;
      if (!dropRemoteOnWirFuersKlima(item)) return false;
      if (item.baseType !== 'RemoteDependencyData') return true;
      const name = (item.baseData as { name?: string; data?: string } | undefined)?.name ?? '';
      const data = (item.baseData as { name?: string; data?: string } | undefined)?.data ?? '';
      const url = typeof name === 'string' ? name : String(data);
      if (url.includes('_rsc=')) return false;
      return true;
    });

    appInsightsInstance = ai;
    return appInsightsInstance;
  });

  return initPromise;
}

export async function getAppInsights(): Promise<AppInsightsInstance | undefined> {
  return initAppInsights();
}

export async function trackClientError(
  error: unknown,
  properties: Record<string, unknown> = {},
  severity: SeverityLevel = 3
): Promise<void> {
  try {
    if (!shouldSendClientTelemetry()) return;
    const ai = await initAppInsights();
    if (!ai) return;
    const err = error instanceof Error ? error : new Error(String(error));
    ai.trackException({ exception: err, severityLevel: severity, properties });
  } catch {
    // never throw from telemetry
  }
}

export async function trackEvent(name: string, properties: Record<string, unknown> = {}): Promise<void> {
  try {
    if (!shouldSendClientTelemetry()) return;
    const ai = await initAppInsights();
    if (!ai) return;
    ai.trackEvent({ name }, properties);
  } catch {
    // never throw from telemetry
  }
}

export async function trackMetric(
  name: string,
  value: number,
  properties: Record<string, unknown> = {}
): Promise<void> {
  try {
    if (!shouldSendClientTelemetry()) return;
    const ai = await initAppInsights();
    if (!ai) return;
    // Application Insights expects metric telemetry with an aggregate shape
    ai.trackMetric({ name, average: value }, properties);
  } catch {
    // never throw from telemetry
  }
}

/**
 * Virtual page view for funnel reports in Azure. Pass step_name consistently (e.g. checkout_step_1).
 * Only field IDs / step names — no PII in properties.
 */
export async function trackPageView(
  pageName: string,
  properties: Record<string, unknown> & { step_name?: string } = {}
): Promise<void> {
  try {
    if (!shouldSendClientTelemetry()) return;
    const ai = await getAppInsights();
    if (!ai) return;
    ai.trackPageView({ name: pageName, properties });
  } catch {
    // never throw from telemetry
  }
}

// Small React hook to ensure initialization on mount when needed
export function useInitClientTelemetry(): void {
  useEffect(() => {
    void initAppInsights();
  }, []);
}
